import { useId, useState } from "react";
import type { MembershipView } from "@starsach/shared-types";
import { useAccess, useGrantRole, useMemberships, useProfiles, useRevokeRole, useRoles } from "../../api/hooks";
import { useStepUp } from "../../auth/StepUpProvider";
import { Hint } from "../../components/Hint";
import { NotHeld } from "../../components/NotHeld";
import { Card, DataTable, Dialog, EmptyState, ErrorState, LoadingState, MutationError, RelativeTime, StatusPill, btnPrimary, btnSecondary, inputClass } from "../../components/ui";
import { RolePills, orderRoles } from "./parts";

/**
 * Who holds what on this environment (ADR-0017 sections 2 and 2b): every
 * membership with its roles, the roles reaching it through groups, and
 * which profiles the person may see. A grant is one dialog - roles as
 * checkboxes, profile visibility beneath - and the API refuses the last
 * manager's demotion, which the screen says before and after.
 */
export function PeopleSection({ environmentId }: { environmentId: string }) {
  const q = useMemberships(environmentId);
  const roles = useRoles();
  const access = useAccess();
  const grantRole = useGrantRole(environmentId);
  const revokeRole = useRevokeRole(environmentId);
  const { ensureFresh } = useStepUp();
  // Section 13.3: access is a security change. The API refuses a stale sign-in with STEP_UP_REQUIRED; asking first is the polite half.
  const revoke = (userId: string) => void ensureFresh().then(() => revokeRole.mutate({ userId }), () => undefined);
  const [editing, setEditing] = useState<{ userId: string; email?: string | undefined; current?: MembershipView } | null>(null);
  const canEdit = access.can(environmentId, "users:edit");

  if (q.isPending) return <LoadingState label="Loading access" />;
  if (q.isError) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  const data = q.data;

  return (
    <Card title={<>Who has access here<Hint id="access.union-rule" label="how several roles combine" /></>}>
      <p className="mb-2 text-sm text-neutral-600 dark:text-neutral-400">
        A role is held per environment, so full authority here says nothing about anywhere else. A person holding several
        roles can do whatever any of them allows; which profiles they may see is set on the person, not the role.
      </p>
      <MutationError of={[grantRole, revokeRole]} className="mb-3" />
      <DataTable
        caption="Everyone holding a role on this environment"
        rows={data.memberships}
        rowKey={(m) => m.userId}
        sortBy={(m) => m.email ?? m.userId}
        empty={<EmptyState title="Nobody holds a role here yet" />}
        columns={[
          { key: "who", header: "Person", render: (m) => m.email ?? m.userId },
          { key: "roles", header: "Roles", render: (m) => <RolePills roleIds={m.roleIds} roles={roles.data?.roles} /> },
          {
            key: "groups",
            header: "Via groups",
            render: (m) =>
              m.viaGroups && m.viaGroups.length > 0 ? (
                <ul className="text-sm">
                  {m.viaGroups.map((g) => (
                    <li key={g.groupId} className="flex flex-wrap items-center gap-1">
                      <span className="text-neutral-600 dark:text-neutral-400">{g.name}:</span> <RolePills roleIds={g.roleIds} roles={roles.data?.roles} muted />
                    </li>
                  ))}
                </ul>
              ) : (
                <span className="text-sm text-neutral-500">-</span>
              ),
          },
          {
            key: "profiles",
            header: <>Profiles<Hint id="access.profile-visibility" label="which profiles a person may see" /></>,
            render: (m) => (m.allProfiles ? <StatusPill status="all" tone="neutral" /> : <StatusPill status={`${m.profileIds?.length ?? 0} chosen`} tone="warning" />),
          },
          { key: "seen", header: "Last signed in", render: (m) => (m.lastSeenAt ? <RelativeTime iso={m.lastSeenAt} /> : <StatusPill status="never" tone="warning" />) },
          { key: "granted", header: "Granted", render: (m) => <>by {m.grantedBy}, <RelativeTime iso={m.grantedAt} /></> },
          {
            key: "actions",
            header: "",
            render: (m) =>
              canEdit ? (
                <span className="flex gap-2">
                  <button type="button" className="text-sm underline disabled:opacity-50" disabled={grantRole.isPending} onClick={() => setEditing({ userId: m.userId, email: m.email, current: m })}>
                    Change
                  </button>
                  <button type="button" className="text-sm text-red-700 underline disabled:opacity-50 dark:text-red-400" disabled={revokeRole.isPending} onClick={() => revoke(m.userId)}>
                    Revoke
                  </button>
                </span>
              ) : null,
          },
        ]}
      />
      {data.managerCount <= 1 && (
        <p role="status" className="mt-2 text-sm text-neutral-600 dark:text-neutral-400">
          One manager holds this environment. Removing or demoting them would leave nobody able to grant a role back, so
          it is refused until a second manager exists.
        </p>
      )}
      {canEdit ? (
        <GrantForm candidates={data.candidates} onChoose={(c) => setEditing(c)} />
      ) : (
        <NotHeld permission="users:edit" className="mt-4 block" />
      )}
      {editing && (
        <GrantDialog
          environmentId={environmentId}
          person={editing}
          onClose={() => setEditing(null)}
          onSubmit={(input) => void ensureFresh().then(() => grantRole.mutate({ userId: editing.userId, ...input }, { onSuccess: () => setEditing(null) }), () => undefined)}
          pending={grantRole.isPending}
          error={grantRole.isError ? grantRole.error : undefined}
        />
      )}
    </Card>
  );
}

function GrantForm({ candidates, onChoose }: { candidates: Array<{ userId: string; email?: string | undefined }>; onChoose: (c: { userId: string; email?: string | undefined }) => void }) {
  const [userId, setUserId] = useState("");
  const id = useId();
  return (
    <form
      className="mt-4 flex flex-wrap items-end gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        const c = candidates.find((x) => x.userId === userId);
        if (c) onChoose(c);
      }}
    >
      <label htmlFor={id} className="text-sm">
        Grant to
        <select id={id} className={`${inputClass} w-64`} value={userId} onChange={(e) => setUserId(e.target.value)}>
          <option value="">Choose someone</option>
          {candidates.map((c) => (
            <option key={c.userId} value={c.userId}>{c.email ?? c.userId}</option>
          ))}
        </select>
      </label>
      <button type="submit" className={btnPrimary} disabled={!userId}>
        Grant…
      </button>
    </form>
  );
}

/**
 * The grant: which roles (any number, built-ins first) and which profiles
 * the person may see here (section 2b). A new membership defaults to every
 * profile; narrowing is the deliberate act.
 */
function GrantDialog({
  environmentId,
  person,
  onClose,
  onSubmit,
  pending,
  error,
}: {
  environmentId: string;
  person: { userId: string; email?: string | undefined; current?: MembershipView };
  onClose: () => void;
  onSubmit: (input: { roleIds: string[]; allProfiles: boolean; profileIds?: string[] }) => void;
  pending: boolean;
  error: unknown;
}) {
  const roles = useRoles();
  const profiles = useProfiles(environmentId);
  const [roleIds, setRoleIds] = useState<Set<string>>(new Set(person.current?.roleIds ?? ["viewer"]));
  const [allProfiles, setAllProfiles] = useState(person.current?.allProfiles ?? true);
  const [profileIds, setProfileIds] = useState<Set<string>>(new Set(person.current?.profileIds ?? []));
  const id = useId();
  const toggle = (set: Set<string>, value: string, on: boolean) => {
    const next = new Set(set);
    if (on) next.add(value);
    else next.delete(value);
    return next;
  };
  return (
    <Dialog title={`${person.current ? "Change" : "Grant"} access for ${person.email ?? person.userId}`} onClose={onClose}>
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit({ roleIds: [...roleIds], allProfiles, ...(allProfiles ? {} : { profileIds: [...profileIds] }) });
        }}
      >
        <fieldset>
          <legend className="text-sm font-medium">Roles on this environment<Hint id="access.roles" label="what a role is" /></legend>
          {roles.isPending && <LoadingState label="Loading roles" />}
          {roles.isError && <ErrorState error={roles.error} />}
          <ul className="mt-2 space-y-1">
            {orderRoles(roles.data?.roles ?? []).map((r) => (
              <li key={r.roleId}>
                <label className="flex items-start gap-2 text-sm">
                  <input type="checkbox" className="mt-1" aria-label={r.name} checked={roleIds.has(r.roleId)} onChange={(e) => setRoleIds(toggle(roleIds, r.roleId, e.target.checked))} />
                  <span>
                    <span className="font-medium">{r.name}</span>
                    {r.builtIn && <span className="ml-1 text-xs text-neutral-500">built in</span>}
                    {r.description && <span className="block text-xs text-neutral-600 dark:text-neutral-400">{r.description}</span>}
                  </span>
                </label>
              </li>
            ))}
          </ul>
        </fieldset>
        <fieldset>
          <legend className="text-sm font-medium">Profiles they may see<Hint id="access.profile-visibility" label="profile visibility" /></legend>
          <div className="mt-2 flex flex-wrap gap-4 text-sm">
            <label className="flex items-center gap-2">
              <input type="radio" name={`${id}-profiles`} checked={allProfiles} onChange={() => setAllProfiles(true)} />
              All profiles, including new ones
            </label>
            <label className="flex items-center gap-2">
              <input type="radio" name={`${id}-profiles`} checked={!allProfiles} onChange={() => setAllProfiles(false)} />
              A chosen set
            </label>
          </div>
          {!allProfiles && (
            <label className="mt-2 block text-sm">
              <span className="sr-only">Profiles</span>
              <select
                multiple
                aria-label="Profiles this person may see"
                className={`${inputClass} h-32`}
                value={[...profileIds]}
                onChange={(e) => setProfileIds(new Set([...e.target.selectedOptions].map((o) => o.value)))}
              >
                {(profiles.data?.profiles ?? []).map((p) => (
                  <option key={p.profileId} value={p.profileId}>{p.name}</option>
                ))}
              </select>
              <span className="mt-1 block text-xs text-neutral-500">A profile added later is not visible to them until someone adds it here.</span>
            </label>
          )}
        </fieldset>
        {error !== undefined && <ErrorState error={error} lead="Couldn’t do that." />}
        <div className="flex gap-2">
          <button type="submit" className={btnPrimary} disabled={pending || roleIds.size === 0 || (!allProfiles && profileIds.size === 0)}>
            {pending ? "Saving…" : person.current ? "Save" : "Grant"}
          </button>
          <button type="button" className={btnSecondary} onClick={onClose}>Cancel</button>
        </div>
      </form>
    </Dialog>
  );
}
